import { cn, getBackgroundClass } from '@workspace/lib';
import { Picture, Typography } from '@workspace/ui/common';
import { getImageDimensions, getAspectRatioClass } from './utils';

interface ImageGridBlockData {
  _key: string;
  _type: 'imageGrid';
  isActive?: boolean;
  imageAspectRatio?: string;
  backgroundColor?: {
    hex: string;
    alpha?: number;
    hsl?: { h: number; s: number; l: number; a?: number };
    hsv?: { h: number; s: number; v: number; a?: number };
    rgb?: { r: number; g: number; b: number; a?: number };
  };
  images: {
    _key: string;
    image: any;
    alt: string;
    caption?: string;
    link?: string;
    openInNewTab?: boolean;
  }[];
}

interface ImageGridProps {
  block: ImageGridBlockData;
  index: number;
}

export function ImageGrid({ block }: ImageGridProps) {
  const { isActive = true, backgroundColor = { hex: '#ffffff' }, images = [], imageAspectRatio } = block;

  if (!isActive) return null;
  if (!images?.length) return null;

  const validImages = images.filter((image) => image?.image);
  if (!validImages.length) {
    return null;
  }

  const desktopImageDimensions = getImageDimensions(validImages.length, imageAspectRatio, false);
  const mobileImageDimensions = getImageDimensions(validImages.length, imageAspectRatio, true);
  const desktopAspectRatioClass = getAspectRatioClass(imageAspectRatio, false);
  const mobileAspectRatioClass = getAspectRatioClass(imageAspectRatio, true);
  const backgroundClass = getBackgroundClass(backgroundColor);

  return (
    <div className={cn('image-grid-color', backgroundClass)}>
      <section className="porta-container px-md py-md md:px-full-banner">
        <div className="">
          {/* Image Grid */}
          <div
            className={cn(
              'flex gap-4 md:gap-lg justify-between',
              validImages.length === 1 ? 'justify-center' : 'flex-wrap',
            )}
          >
            {validImages?.map((image, index) => {
              if (!image?.image) {
                console.warn(`Image at index ${index} is missing image data:`, image);
                return null;
              }

              const Wrapper = image.link ? 'a' : 'div';
              const linkProps = image.link
                ? {
                    href: image.link,
                    ...(image.openInNewTab && { target: '_blank', rel: 'noopener noreferrer' }),
                  }
                : {};

              return (
                <Wrapper
                  key={image._key || index}
                  className={cn(
                    'group relative overflow-hidden',
                    image.link && 'cursor-pointer transition-transform block',
                    validImages.length === 1
                      ? cn('w-full md:w-242.5', mobileAspectRatioClass, desktopAspectRatioClass)
                      : cn('w-full md:w-[calc(50%-16px)]', mobileAspectRatioClass, desktopAspectRatioClass),
                  )}
                  {...linkProps}
                >
                  <div className={cn('w-full h-full overflow-hidden')}>
                    <Picture
                      image={image.image}
                      alt={image.image?.bynderImage?.alt || image.image?.alt || image.alt || 'Image'}
                      desktopWidth={desktopImageDimensions.width}
                      desktopHeight={desktopImageDimensions.height}
                      mobileWidth={mobileImageDimensions.width}
                      mobileHeight={mobileImageDimensions.height}
                      imgClassName="w-full h-full object-cover transition-transform duration-300"
                    />
                  </div>

                  {/* Caption overlay */}
                  {image.caption && (
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-3 transform translate-y-full transition-transform duration-300 group-hover:translate-y-0">
                      <Typography variant="bodySm" className="text-white">
                        {image.caption}
                      </Typography>
                    </div>
                  )}

                  {/* Link indicator */}
                  {image.link && (
                    <div className="absolute top-3 right-3 bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <svg
                        className="w-4 h-4 text-gray-700"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </div>
                  )}
                </Wrapper>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
